<template>
  <div
    class="amprod-option-card"
    :class="{
      selected: selected,
      incompatible: disabled,
    }"
    @click="handleSelect"
    @mouseenter="handleMouseEnter"
    @mouseleave="showTooltip = false"
  >
    <div v-if="option.image" class="amprod-option-image">
      <img :src="option.image" :alt="option.part_number" />
    </div>

    <div class="amprod-option-description">
      {{ option.description }}
    </div>

    <div class="amprod-option-part-number">
      {{ option.part_number }}
    </div>

    <div v-if="hasDetails" class="amprod-option-details">
      <div v-if="option.voltage" class="option-voltage">
        Voltage: {{ option.voltage }}
      </div>
      <div v-if="option.size" class="option-size">Size: {{ option.size }}</div>
    </div>

    <div v-if="incompatibilityReason" class="incompatibility-badge">
      <span class="incompatibility-icon">!</span>
    </div>

    <!-- Quantity controls - only show when selected and unlimited quantities allowed -->
    <div v-if="selected && allowQuantity" class="amprod-quantity-controls">
      <QuantityDisplay
        :quantity="quantity"
        label="Quantity:"
        variant="controls"
        :allow-decrease="quantity > 0"
        :min-quantity="0"
        class="amprod-quantity-component"
        @decrease="decreaseQuantity"
        @increase="increaseQuantity"
        @click.stop
      />
    </div>

    <!-- HTML Tooltip -->
    <div v-if="showTooltip && incompatibilityReason" class="amprod-tooltip">
      {{ incompatibilityReason }}
    </div>
  </div>
</template>

<script>
import QuantityDisplay from "./common/QuantityDisplay.vue";

export default {
  name: "OptionCard",
  components: {
    QuantityDisplay,
  },
  props: {
    option: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    incompatibilityReason: {
      type: String,
      default: null,
    },
    allowQuantity: {
      type: Boolean,
      default: false,
    },
    quantity: {
      type: Number,
      default: 1,
    },
  },
  emits: ["select", "quantity-change"],
  data() {
    return {
      showTooltip: false,
    };
  },
  computed: {
    hasDetails() {
      return this.option.voltage || this.option.size;
    },
  },
  methods: {
    handleMouseEnter() {
      // Only show tooltip if there's an incompatibility reason
      if (this.incompatibilityReason) {
        this.showTooltip = true;
      }
    },
    handleSelect() {
      if (!this.disabled) {
        this.$emit("select", this.option);
      }
    },
    increaseQuantity() {
      const newQuantity = this.quantity + 1;
      this.$emit("quantity-change", this.option, newQuantity);
    },
    decreaseQuantity() {
      if (this.quantity > 0) {
        const newQuantity = this.quantity - 1;
        this.$emit("quantity-change", this.option, newQuantity);

        // If quantity reaches 0, deselect the option
        if (newQuantity === 0) {
          this.$emit("select", this.option);
        }
      }
    },
    updateQuantity(event) {
      const newQuantity = Math.max(0, parseInt(event.target.value) || 0);
      if (newQuantity !== this.quantity) {
        this.$emit("quantity-change", this.option, newQuantity);

        // If quantity reaches 0, deselect the option
        if (newQuantity === 0) {
          this.$emit("select", this.option);
        }
      }
    },
  },
};
</script>

<style scoped>
/* Ensure the option card has relative positioning for tooltip positioning */
.amprod-option-card {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 15px;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  background-color: #ffffff;
}

.amprod-option-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: #bbbbbb;
}

.amprod-option-card.selected {
  border: 2px solid #0073aa;
  box-shadow: 0 0 0 2px rgba(0, 115, 170, 0.2);
}

.amprod-option-card.incompatible {
  opacity: 0.6;
  cursor: not-allowed;
  filter: grayscale(60%);
}

.amprod-option-image {
  margin-bottom: 10px;
  text-align: center;
}

.amprod-option-image img {
  max-width: 100%;
  max-height: 150px;
  object-fit: contain;
}

.amprod-option-description {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 8px;
}

.amprod-option-part-number {
  font-size: 14px;
  color: #555;
  margin-bottom: 10px;
}

.amprod-option-details {
  font-size: 13px;
  color: #666;
  margin-top: 10px;
}

.incompatibility-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #ff5722;
  display: flex;
  align-items: center;
  justify-content: center;
}

.incompatibility-icon {
  color: white;
  font-weight: bold;
}

.amprod-quantity-controls {
  border-top: 1px solid #e0e0e0;
}

.amprod-quantity-component {
  padding-top: 15px;
}

/* HTML Tooltip Styles */
.amprod-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(220, 53, 69, 0.95);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
  margin-bottom: 8px;
  max-width: 250px;
  white-space: normal;
  text-align: center;
  line-height: 1.3;
  opacity: 1 !important;
  filter: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.amprod-tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid rgba(220, 53, 69, 0.95);
}

/* Ensure the option card has relative positioning for tooltip positioning */
.amprod-option-card {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 15px;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  background-color: #ffffff;
}
</style>
